import type { Worm } from '../entities/worm';
import type { Projectile } from '../entities/projectile';
import type { OilBarrel } from '../entities/oilBarrel';
import type { Vector2 } from '../core/constants';

export interface BarrelCollisionResult {
  barrel: OilBarrel;
  normal: Vector2;
}

export function checkWormBarrelCollision(worm: Worm, barrels: OilBarrel[]): OilBarrel | null {
  if (worm.state === 'dead') return null;

  const wormBox = worm.getBounds();

  for (const barrel of barrels) {
    if (!barrel.active) continue;

    const barrelBox = barrel.getBounds();

    const overlapX =
      Math.min(wormBox.x + wormBox.width, barrelBox.x + barrelBox.width) -
      Math.max(wormBox.x, barrelBox.x);
    const overlapY =
      Math.min(wormBox.y + wormBox.height, barrelBox.y + barrelBox.height) -
      Math.max(wormBox.y, barrelBox.y);

    if (overlapX > 0 && overlapY > 0) {
      return barrel;
    }
  }

  return null;
}

export function resolveWormBarrelCollision(worm: Worm, barrel: OilBarrel): void {
  const wormBox = worm.getBounds();
  const barrelBox = barrel.getBounds();

  const wormCenterX = wormBox.x + wormBox.width / 2;
  const wormCenterY = wormBox.y + wormBox.height / 2;
  const barrelCenterX = barrelBox.x + barrelBox.width / 2;
  const barrelCenterY = barrelBox.y + barrelBox.height / 2;

  const overlapX =
    Math.min(wormBox.x + wormBox.width, barrelBox.x + barrelBox.width) -
    Math.max(wormBox.x, barrelBox.x);
  const overlapY =
    Math.min(wormBox.y + wormBox.height, barrelBox.y + barrelBox.height) -
    Math.max(wormBox.y, barrelBox.y);

  if (overlapX < overlapY) {
    if (wormCenterX < barrelCenterX) {
      worm.position.x -= overlapX;
    } else {
      worm.position.x += overlapX;
    }
    worm.velocity.x = 0;
  } else {
    if (wormCenterY < barrelCenterY) {
      worm.position.y -= overlapY;
      worm.velocity.y = 0;
      worm.isGrounded = true;
    } else {
      worm.position.y += overlapY;
      worm.velocity.y = 0;
    }
  }
}

export function checkProjectileBarrelCollision(
  projectile: Projectile,
  barrels: OilBarrel[],
): BarrelCollisionResult | null {
  for (const barrel of barrels) {
    if (!barrel.active) continue;

    const box = barrel.getBounds();

    if (
      projectile.position.x >= box.x &&
      projectile.position.x <= box.x + box.width &&
      projectile.position.y >= box.y &&
      projectile.position.y <= box.y + box.height
    ) {
      const centerX = box.x + box.width / 2;
      const centerY = box.y + box.height / 2;

      let nx = projectile.position.x - centerX;
      let ny = projectile.position.y - centerY;
      const len = Math.sqrt(nx * nx + ny * ny);

      if (len > 0) {
        nx /= len;
        ny /= len;
      } else {
        nx = 0;
        ny = -1;
      }

      return { barrel, normal: { x: nx, y: ny } };
    }
  }

  return null;
}

export function handleProjectileBarrelHit(
  projectile: Projectile,
  result: BarrelCollisionResult,
): 'explode' | 'bounce' {
  const { normal } = result;

  if (projectile.type === 'bazooka' || projectile.type === 'clusterlet') {
    return 'explode';
  }

  const dot = projectile.velocity.x * normal.x + projectile.velocity.y * normal.y;
  projectile.velocity.x = (projectile.velocity.x - 2 * dot * normal.x) * projectile.bounceH;
  projectile.velocity.y = (projectile.velocity.y - 2 * dot * normal.y) * projectile.bounceV;

  projectile.position.x += normal.x * 3;
  projectile.position.y += normal.y * 3;

  projectile.bounceCount++;

  const speed = Math.sqrt(projectile.velocity.x ** 2 + projectile.velocity.y ** 2);
  if (speed < 30) {
    projectile.isOnGround = true;
  }
  if (speed < 5) {
    projectile.velocity.x = 0;
    projectile.velocity.y = 0;
  }

  return 'bounce';
}
